import { Navigate, NavLink, Outlet, useLocation } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { ApiError, authClient } from "../api.ts";
import { useBrand, useMe, useSettings } from "../hooks.ts";
import { useSaveSettings } from "../pages/Settings.tsx";
import { browserZone } from "../lib/cron.ts";
import { ADMIN_GROUPS } from "../lib/admin-nav.ts";
import { useEffect, useRef } from "react";
import { usePendingApprovals } from "../pages/Approvals.tsx";
import { cx, Spinner } from "./ui.tsx";
import { ImpersonationBanner } from "./impersonation.tsx";

const nav = {
  user: [
    { to: "/chat", label: "Chat" },
    { to: "/approvals", label: "Approvals" },
    { to: "/dashboard", label: "Dashboard" },
    { to: "/files", label: "Files" },
    { to: "/tasks", label: "Scheduled tasks" },
    { to: "/skills", label: "Skills" },
    { to: "/connections", label: "Connected apps" },
    { to: "/mcp", label: "MCP servers" },
    { to: "/secrets", label: "Secrets" },
    { to: "/settings", label: "Settings" },
  ],
  admin: ADMIN_GROUPS.map((g) => ({ to: `/admin/${g.base}`, label: g.label })),
};

/** The logo sits on the dark sidebar colour: Krut's white wordmark, the install's own, or its name. */
export function Logo({ className = "h-6" }: { className?: string }) {
  const b = useBrand();
  if (b.loading) return <span className={cx("inline-block", className)} />;
  if (b.logoUrl) return <img src={b.logoUrl} alt={b.name} className={cx("w-auto max-w-full select-none object-contain", className)} draggable={false} />;
  if (b.custom) return <span className={cx("flex items-center text-lg font-semibold tracking-tight text-white", className)}>{b.name}</span>;
  return <img src="/krut-logo.png" alt={b.name} className={cx("w-auto select-none", className)} draggable={false} />;
}

/** Keeps the tab title, icon and colours in step with Admin → Branding (also after a save). */
export function BrandEffects() {
  const b = useBrand();
  useEffect(() => {
    if (b.loading) return;
    document.title = b.name;
    const root = document.documentElement.style;
    root.setProperty("--color-accent", b.accentColor);
    root.setProperty("--chrome", b.sidebarColor);
    root.setProperty("--chrome-2", `color-mix(in srgb, ${b.sidebarColor} 88%, white)`);
    for (const rel of ["icon", "apple-touch-icon"]) {
      const link = document.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
      if (link) {
        link.removeAttribute("type");
        link.href = b.faviconUrl;
      }
    }
    document.querySelector('meta[name="theme-color"]')?.setAttribute("content", b.sidebarColor);
  }, [b.loading, b.name, b.accentColor, b.sidebarColor, b.faviconUrl]);
  return null;
}

/**
 * Until someone picks a time zone, their assistant takes this browser's (once, on their own sign-in —
 * never while an admin is viewing as them). They can change it in Settings.
 */
function useAdoptBrowserZone(enabled: boolean) {
  const settings = useSettings();
  const save = useSaveSettings();
  const tried = useRef(false);
  useEffect(() => {
    if (!enabled || tried.current || !settings.data || settings.data.timezone !== null) return;
    tried.current = true;
    save.mutate({ timezone: browserZone() });
  }, [enabled, settings.data, save]);
}

export function Shell() {
  const me = useMe();
  useAdoptBrowserZone(!!me.data && !me.data.user.impersonatedBy);
  const approvals = usePendingApprovals();
  const qc = useQueryClient();
  const loc = useLocation();
  if (me.isPending) return <div className="grid h-full place-items-center"><Spinner /></div>;
  // Remember where they were going (e.g. a /link/… page from a chat app) and return after sign-in.
  if (me.error instanceof ApiError && me.error.status === 401)
    return <Navigate to={loc.pathname === "/" ? "/login" : `/login?next=${encodeURIComponent(loc.pathname + loc.search)}`} replace />;
  if (me.error) return <div className="grid h-full place-items-center text-sm text-[var(--muted)]">{me.error.message}</div>;
  const { user } = me.data;
  if (loc.pathname.startsWith("/admin") && user.role !== "admin") return <Navigate to="/" replace />;
  // Chat is a full-height app surface; every other page is a padded, centred document.
  const fullBleed = loc.pathname.startsWith("/chat");
  const imp = user.impersonatedBy;

  const link = (i: { to: string; label: string; end?: boolean }) => (
    <NavLink
      key={i.to}
      to={i.to}
      end={i.end}
      className={({ isActive }) =>
        cx(
          "block whitespace-nowrap rounded-lg px-3 py-2 text-sm transition",
          isActive ? "bg-white/10 font-medium text-white" : "text-[var(--chrome-text)] hover:bg-white/5 hover:text-white",
        )
      }
    >
      {i.label}
      {i.to === "/approvals" && !!approvals.data?.pending && (
        <span className="ml-2 inline-grid min-w-5 place-items-center rounded-full bg-amber-400 px-1.5 text-[11px] font-semibold text-black">{approvals.data.pending}</span>
      )}
    </NavLink>
  );

  return (
    <>
    {imp && <ImpersonationBanner asUser={{ name: user.name, email: user.email }} mode={imp.mode} adminEmail={imp.email} expiresAt={imp.expiresAt} />}
    <div className={cx("flex flex-col md:flex-row", fullBleed ? (imp ? "h-[calc(100dvh-2.5rem)]" : "h-dvh") : "min-h-full")}>
      <aside className={cx("flex shrink-0 flex-col bg-[var(--chrome)] p-4 md:sticky md:w-60", imp ? "md:top-10 md:h-[calc(100vh-2.5rem)]" : "md:top-0 md:h-screen")}>
        <div className="mb-6 px-2 pt-1"><Logo /></div>
        <nav className="flex gap-1 overflow-x-auto md:block md:space-y-0.5">
          {nav.user.map(link)}
          {user.role === "admin" && (
            <>
              <div className="hidden px-3 pt-5 pb-1.5 text-xs font-medium uppercase tracking-wider text-white/40 md:block">Admin</div>
              {nav.admin.map(link)}
            </>
          )}
        </nav>
        <div className="mt-auto hidden items-center gap-3 border-t border-white/10 px-1 pt-4 md:flex">
          {user.image ? <img src={user.image} alt="" className="h-8 w-8 rounded-full" /> : <div className="grid h-8 w-8 place-items-center rounded-full bg-white/10 text-xs font-semibold text-white">{user.email[0]?.toUpperCase()}</div>}
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-medium text-white">{user.name}</div>
            <div className="truncate text-xs text-[var(--chrome-text)]">{user.email}</div>
          </div>
          <button
            title="Sign out"
            className="rounded-md p-1.5 text-[var(--chrome-text)] hover:bg-white/10 hover:text-white"
            onClick={async () => {
              await authClient.signOut();
              qc.clear();
              location.href = "/login";
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" /></svg>
          </button>
        </div>
      </aside>
      {fullBleed ? (
        <main className="min-h-0 min-w-0 flex-1"><Outlet /></main>
      ) : (
        <main className="min-w-0 flex-1 px-4 py-6 md:px-10 md:py-10">
          <div className="mx-auto max-w-5xl"><Outlet /></div>
        </main>
      )}
    </div>
    </>
  );
}
